import { Button, Menu, MantineProvider } from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { IconFileTypePdf } from "@tabler/icons-react";
import dayjs from "dayjs";
import { useCallback } from "react";
import { renderToStaticMarkup } from "react-dom/server";
import { useFormContext } from "react-hook-form";
import { Provider } from "react-redux";
import { MemoryRouter } from "react-router-dom";
import Entry from "src/components/shared/EntriesGenerator/Entry";
import { usePostApi, useTheme } from "src/hooks";
import useNonMaritime from "src/hooks/useNonMaritime";
import { createPdfHead } from "src/mock/createPdfHead";
import { type IReportEntry } from "src/pages/Reports/Report/Report.types";
import {
  getPdfBody,
  getPdfSize,
  renderReviewStatus,
} from "src/pages/Reports/Report/Report.utils";
import { useSelector } from "src/redux/hooks";
import { store } from "src/redux/store";
import { insertValues } from "src/redux/utils/insertValues";
import { Notification } from "src/utils/Notification";

interface Props {
  hamburger?: boolean;
}

const PdfEntryButton = ({ hamburger }: Props) => {
  const { post, isLoading } = usePostApi();
  const theme = useTheme();
  const { latestEntry, lbentry, entryDataToSave } = useSelector(
    state => state.generatorSlice,
  );
  const { getValues } = useFormContext();
  const { replaceMaritimeText } = useNonMaritime();

  const renderEntryTitle = useCallback((entry: IReportEntry) => {
    return () => (
      <p style={{ margin: 0 }}>{`Entry Ref. #${entry.unum ? entry.unum : ""}${
        entry.enum ? (entry.unum ? " - " + entry.enum : entry.enum) : ""
      } / ${entry.lbname} / ${dayjs(entry.datecreated).format(
        "DD MMM YYYY - HH:mm",
      )} LT / ${dayjs(entry.datecreatedUTC).format(
        "HH:mm",
      )} UTC / ${replaceMaritimeText(renderReviewStatus(entry.status))}`}</p>
    );
  }, []);

  const handlePrint = useCallback(async () => {
    try {
      const values = getValues();
      const filledSections = insertValues(values, lbentry.sections);
      const entryData = {
        lbeid: latestEntry?.id || "",
        lbname: latestEntry?.lbName || "",
        unum: "",
        enum: latestEntry?.entryref || "",
        status: latestEntry?.status || 0,
        reason: "",
        lbentry: { ...lbentry, sections: filledSections },
        datecreated: entryDataToSave?.datecreated,
        datecreatedUTC: entryDataToSave?.datecreatedUTC,
        idata: "",
      } as unknown as IReportEntry;

      const staticHtml = renderToStaticMarkup(
        <Provider store={store}>
          <MantineProvider theme={theme as any}>
            <MemoryRouter>
              <Entry
                entry={entryData}
                renderTitle={renderEntryTitle(entryData)}
                pdfEntry
              />
            </MemoryRouter>
          </MantineProvider>
        </Provider>,
      );

      const div = document.createElement("div");
      div.innerHTML = staticHtml;
      document.body.appendChild(div);
      const contentWidth = div.offsetWidth;
      document.body.removeChild(div);

      const orientation = contentWidth > 1400 ? "landscape" : "portrait";

      const body = await getPdfBody(div);
      const pdfParams = {
        head: createPdfHead,
        name: latestEntry?.lbName || "Entry",
        body,
        scale: 0.8,
        ...getPdfSize(orientation),
      };

      const fileData = await post({
        request: "create-pdf",
        body: {
          params: pdfParams,
        },
      });

      if (window.api) {
        window.api.downloadFile(
          fileData.file,
          ({ full, path }) => {
            notifications.show({
              title: "Report Downloaded",
              message: (
                <div>
                  <Button
                    variant="subtle"
                    compact
                    onClick={() => {
                      if (window.api) window.api.openFile(full);
                    }}>
                    Open File
                  </Button>
                  <Button
                    variant="subtle"
                    compact
                    onClick={() => {
                      if (window.api) window.api.openFile(path);
                    }}>
                    Open Folder
                  </Button>
                </div>
              ),
            });
          },
          "reports",
        );
      } else {
        window.open(fileData.file, "_blank");
      }
    } catch (e) {
      console.error(e);
      Notification("Error", "Failed to generate PDF", "red");
    }
  }, [post, latestEntry, lbentry, entryDataToSave]);

  if (hamburger) {
    return (
      <Menu.Item
        disabled={isLoading}
        onClick={handlePrint}
        leftSection={<IconFileTypePdf size={20} />}>
        Generate PDF
      </Menu.Item>
    );
  }

  return null;
};

export default PdfEntryButton;
